/**
 * Arena collision data (docs/04, docs/07): static triangles from the Blender `COL_*` meshes and
 * the `SPAWN_*` markers. The same binary ships to the client and the server.
 *
 * Layout (little-endian): magic "ACOL", u32 version, u32 vertexCount, u32 indexCount,
 * u32 spawnCount, f32 vertices[3n], u32 indices[m], then spawns: u8 nameLen, utf8 name,
 * f32 x, y, z, yaw.
 */
export interface Spawn {
  name: string;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface ArenaCollision {
  vertices: Float32Array;
  indices: Uint32Array;
  spawns: Spawn[];
}

const MAGIC = 0x4c4f4341; // "ACOL"
const VERSION = 1;

export function encodeArenaCollision(arena: ArenaCollision): Uint8Array {
  // Spawn names are ASCII marker names (SPAWN_A); the sim has no DOM or Node text APIs.
  const names = arena.spawns.map((s) => Uint8Array.from(s.name, (ch) => ch.charCodeAt(0) & 0x7f));
  const size =
    20 +
    arena.vertices.length * 4 +
    arena.indices.length * 4 +
    names.reduce((n, b) => n + 1 + b.length + 16, 0);
  const buf = new ArrayBuffer(size);
  const view = new DataView(buf);
  let o = 0;
  const u32 = (v: number) => {
    view.setUint32(o, v, true);
    o += 4;
  };
  const f32 = (v: number) => {
    view.setFloat32(o, v, true);
    o += 4;
  };
  u32(MAGIC);
  u32(VERSION);
  u32(arena.vertices.length / 3);
  u32(arena.indices.length);
  u32(arena.spawns.length);
  for (const v of arena.vertices) f32(v);
  for (const i of arena.indices) u32(i);
  arena.spawns.forEach((s, k) => {
    const name = names[k] as Uint8Array;
    view.setUint8(o++, name.length);
    new Uint8Array(buf, o, name.length).set(name);
    o += name.length;
    f32(s.x);
    f32(s.y);
    f32(s.z);
    f32(s.yaw);
  });
  return new Uint8Array(buf);
}

export function decodeArenaCollision(bytes: Uint8Array): ArenaCollision {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let o = 0;
  const u32 = () => {
    const v = view.getUint32(o, true);
    o += 4;
    return v;
  };
  const f32 = () => {
    const v = view.getFloat32(o, true);
    o += 4;
    return v;
  };
  if (u32() !== MAGIC) throw new Error('not an arena collision file');
  const version = u32();
  if (version !== VERSION) throw new Error(`arena collision version ${version} unsupported`);
  const vertexCount = u32();
  const indexCount = u32();
  const spawnCount = u32();
  const vertices = new Float32Array(vertexCount * 3);
  for (let i = 0; i < vertices.length; i++) vertices[i] = f32();
  const indices = new Uint32Array(indexCount);
  for (let i = 0; i < indices.length; i++) indices[i] = u32();
  const spawns: Spawn[] = [];
  for (let i = 0; i < spawnCount; i++) {
    const len = view.getUint8(o++);
    const name = String.fromCharCode(...bytes.subarray(o, o + len));
    o += len;
    spawns.push({ name, x: f32(), y: f32(), z: f32(), yaw: f32() });
  }
  return { vertices, indices, spawns };
}

/** A flat square floor with a ring of wall boxes: the test arena and the pre-greybox fallback. */
export function createTestArena(radius = 20, wallHeight = 3): ArenaCollision {
  const vertices: number[] = [];
  const indices: number[] = [];
  const quad = (a: number[], b: number[], c: number[], d: number[]) => {
    const base = vertices.length / 3;
    vertices.push(...a, ...b, ...c, ...d);
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  };
  const r = radius + 4;
  quad([-r, 0, -r], [-r, 0, r], [r, 0, r], [r, 0, -r]);
  const segments = 24;
  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    const p0 = [Math.cos(a0) * radius, 0, Math.sin(a0) * radius];
    const p1 = [Math.cos(a1) * radius, 0, Math.sin(a1) * radius];
    // Inward-facing wall (counter-clockwise seen from the arena center).
    quad(
      [p0[0] as number, 0, p0[2] as number],
      [p0[0] as number, wallHeight, p0[2] as number],
      [p1[0] as number, wallHeight, p1[2] as number],
      [p1[0] as number, 0, p1[2] as number],
    );
  }
  return {
    vertices: new Float32Array(vertices),
    indices: new Uint32Array(indices),
    spawns: [
      { name: 'SPAWN_A', x: 0, y: 0, z: -6, yaw: 0 },
      { name: 'SPAWN_B', x: 0, y: 0, z: 6, yaw: Math.PI },
    ],
  };
}
